import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AsyncPipe, NgClass, NgFor, NgIf, NgTemplateOutlet} from '@angular/common';
import {BehaviorSubject, Observable, Subject, combineLatest} from 'rxjs';
import {MatCheckbox, MatCheckboxModule} from '@angular/material/checkbox';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';
import {MatButtonModule} from '@angular/material/button';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatToolbarModule} from '@angular/material/toolbar';
import {TranslateModule} from '@ngx-translate/core';
import * as _ from 'lodash';
import {
  DialogWidth,
  Q9TrackDirective,
  Q9HtmlEncoderPipe,
  SnackBarService
} from '@q9elements/ui-kit/common';
import {
  IconButtonComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent
} from '@q9elements/ui-kit/components';
import {NavObject, NavigationService} from '../../../core/navigation/navigation.service';
import {
  AuthorisationService,
  IAuthorisationTask,
  ITaskAccessResponse
} from '../../../core/services/authorisation.service';
import {PopupManagerService} from '../../../core/services/popup-manager.service';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {BACKEND_ERROR_STATUSES} from '../../../shared/constants/backend-error-status.constants';
import {
  AUTHORISATION_TASK_STATUSES_ENUM,
  CLOSED_AUTHORISATION_TASK_STATUSES
} from '../../../shared/constants/diagram-authorisation.constants';
import {RejectTaskDialogComponent} from '../../dialogs/reject-task-dialog/reject-task-dialog.component';
import {AuthorisationPopupComponent} from '../../shared/authorisation-popup/authorisation-popup.component';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../shared/models/diagram-version.constant';
import {SignOffAuthorisationComponent} from '../../dialogs/sign-off-authorisation/sign-off-authorisation.component';

@Component({
  selector: 'app-authorisation-navigation',
  templateUrl: './authorisation-navigation.component.html',
  styleUrls: ['./authorisation-navigation.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgTemplateOutlet,
    NgClass,
    MatCheckboxModule,
    MatTooltipModule,
    MatButtonModule,
    MatToolbarModule,
    MatMenuModule,
    MatIconModule,
    MatSlideToggleModule,
    AuthorisationPopupComponent,
    NgFor,
    MatDividerModule,
    AsyncPipe,
    TranslateModule,
    Q9HtmlEncoderPipe,
    Q9TrackDirective,
    SecondaryButtonComponent,
    IconButtonComponent,
    PrimaryButtonComponent
  ]
})
export class AuthorisationNavigationComponent implements OnInit, OnDestroy {
  @ViewChild('rejectInputElement', {static: false}) rejectCheckedRef: ElementRef<MatCheckbox>;
  @ViewChild('acceptInputElement', {static: false}) acceptCheckedRef: ElementRef<MatCheckbox>;
  @ViewChild('signOffInputElement', {static: false}) signOffCheckedRef: ElementRef<MatCheckbox>;

  private destroyed$ = new Subject<boolean>();
  private currDv$ = this.initCurrentDiagramVersion();
  private lastVisited: {diagramId: string; state: string; authorisation: string};
  private currTask$: BehaviorSubject<IAuthorisationTask> = new BehaviorSubject(undefined);

  public enabled$ = new BehaviorSubject(false);
  public authorisationMode$ = this.authorisationService.authorisationMode$;
  public taskList$: Observable<IAuthorisationTask[]> = this.authorisationService.taskList$;
  public current$: Observable<IAuthorisationTask> = this.initCurrentTask();
  public currentPosition$ = this.initCurrentPosition();
  public outOfBounds$: Observable<boolean> = this.initOutsideBound();
  public taskClosed$ = this.authorisationService.taskClosed$;
  public userHistory = [];
  public preventClicks = false;
  public hasNext: boolean;
  public hasPrev: boolean;
  public readonly AUTHORISATION_TASK_STATUSES = AUTHORISATION_TASK_STATUSES_ENUM;

  constructor(
    private authorisationService: AuthorisationService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private navigationSvc: NavigationService,
    private popupSvc: PopupManagerService,
    private snackBarSvc: SnackBarService
  ) {}

  ngOnInit() {
    this.initAuthorisation();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private initAuthorisation() {
    combineLatest([this.authorisationMode$, this.taskList$])
      .pipe(
        tap<any>(([authorisationMode, taskList]) => {
          const authorisationWithTasks = authorisationMode && taskList && taskList.length > 0;

          this.enabled$.next(authorisationWithTasks);
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe();
  }

  private initCurrentDiagramVersion() {
    return this.diagramJsonSvc.diagramJson$.pipe(
      filter(x => Boolean(x.diagramId)),
      distinctUntilChanged((dvA, dvB) => dvA.id === dvB.id)
    );
  }

  private initCurrentTask(): Observable<IAuthorisationTask> {
    let diagramVersionWithTask = false;

    return combineLatest([
      this.authorisationService.currentTask$,
      this.diagramJsonSvc.diagramJson$
    ]).pipe(
      map(([currentTask, diagramJson]) => {
        diagramVersionWithTask = currentTask
          ? currentTask.connection.diagramId === diagramJson.diagramId
          : false;

        return currentTask;
      }),
      tap(currentTask => this.setCurrentTask(currentTask)),
      tap(currentTask => {
        if (diagramVersionWithTask) {
          this.setLastVisited(currentTask);
        }
      }),
      tap(currentTask => this.setNavKeyAvailability(currentTask)),
      tap(() => (this.preventClicks = false)),
      shareReplay(),
      takeUntil(this.destroyed$)
    ) as Observable<IAuthorisationTask>; // retryWhen adds Observable<{}> to possible return values but never returns it in this case
  }

  private initCurrentPosition() {
    return this.current$.pipe(
      map(curr => {
        const arr = this.authorisationService.taskListSync();
        const index = arr.indexOf(curr);

        return index + 1;
      })
    );
  }

  private initOutsideBound() {
    return this.current$.pipe(
      switchMap(() => combineLatest(this.currDv$, this.taskList$)),
      map(([diagramVersion, taskList]) => {
        const task = this.currTask$.value;

        if (!task) {
          return true;
        }

        const result = this.findAuthorisationItem(taskList, diagramVersion.diagramId);

        return !result;
      })
    );
  }

  private setCurrentTask(task) {
    this.currTask$.next(task);
  }

  private setLastVisited(task) {
    if (!task) {
      return;
    }

    const lastVisitedDiagram = this.diagramJsonSvc.get();
    const authorisationStr = CLOSED_AUTHORISATION_TASK_STATUSES.includes(task.status)
      ? task._id
      : 'true';
    const lastVisited = {
      diagramId: lastVisitedDiagram.diagramId,
      state: lastVisitedDiagram.state,
      authorisation: authorisationStr
    };

    this.lastVisited = lastVisited;
  }

  private setNavKeyAvailability(curr) {
    const arr = this.authorisationService.taskListSync();
    const index = arr.indexOf(curr);

    this.hasNext = Boolean(arr[index + 1]);
    this.hasPrev = Boolean(arr[index - 1]);
  }

  private findCurrentDiagramIndex(arr: Array<IAuthorisationTask>) {
    return arr.indexOf(this.findCurrentDiagram(arr));
  }

  private findCurrentDiagram(arr: Array<IAuthorisationTask>) {
    const diagramId = this.diagramJsonSvc.get().diagramId;

    return this.findAuthorisationItem(arr, diagramId);
  }

  private findAuthorisationItem(arr, diagramId) {
    return arr.find(item => {
      return item.diagram === diagramId;
    });
  }

  public isTaskInInvalidStatus(currentTask): boolean {
    return (
      currentTask?.status === AUTHORISATION_TASK_STATUSES_ENUM.REJECTED ||
      (currentTask?.status !== AUTHORISATION_TASK_STATUSES_ENUM.NOT_STARTED &&
        currentTask?.status !== AUTHORISATION_TASK_STATUSES_ENUM.IN_PROGRESS)
    );
  }

  public rejectTask() {
    const currentTask = this.currTask$.value;
    const taskList = this.authorisationService.taskListSync();
    const {diagram: diagramId} = currentTask;

    this.popupSvc
      .open(RejectTaskDialogComponent, {
        panelClass: ['q9-dialog'],
        width: '400px',
        data: diagramId
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(taskResponse => {
        const taskToNavigate = taskResponse
          ? this.selectTaskToNavigate(taskResponse, taskList)
          : null;

        switch (true) {
          case !taskResponse:
            break;

          case taskResponse.error && this.hasNext:

          case this.hasNext:
            this.navNext();
            break;

          case taskResponse.error && !!taskToNavigate:

          case !!taskToNavigate:
            this.processNavigation(taskToNavigate.diagram);
            break;

          default:
            this.exitAuthorisation();
            break;
        }
      });
  }

  public acceptTask(check: boolean) {
    this.preventClicks = true;
    const currentTask = {...this.currTask$.value};
    const taskList = this.authorisationService.taskListSync();
    const {diagram: diagramId, status: taskStatus} = currentTask;
    const newTaskStatus = this.getNewTaskStatus({check, taskStatus});

    if (!newTaskStatus) {
      return;
    }

    this.authorisationService
      .approveTask({diagramId, taskStatus: newTaskStatus})
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (value: ITaskAccessResponse) => {
          this.authorisationService.changeCurrentTaskStatus(newTaskStatus);
          this.preventClicks = false;
          const taskToNavigate = this.selectTaskToNavigate(currentTask, taskList);

          switch (true) {
            case value.lastApprovedTask:
              this.signOffAllTask(value.numberOfDiagrams);
              break;

            case this.hasNext &&
              newTaskStatus === AUTHORISATION_TASK_STATUSES_ENUM.READY_TO_APPROVE:
              this.navNext();
              break;

            case !!taskToNavigate &&
              newTaskStatus === AUTHORISATION_TASK_STATUSES_ENUM.READY_TO_APPROVE:
              this.processNavigation(taskToNavigate.diagram);
              break;

            default:
              break;
          }
        },
        reject => {
          this.snackBarSvc.error(reject.error);
          const taskToNavigate = reject ? this.selectTaskToNavigate(currentTask, taskList) : null;

          switch (true) {
            case reject.status === BACKEND_ERROR_STATUSES.TASK_SAME_STATUS:
              this.processNavigation(this.authorisationService.currentTaskSync().diagram);
              break;

            case this.hasNext:
              this.navNext();
              break;

            case !!taskToNavigate:
              this.processNavigation(taskToNavigate.diagram);
              break;

            default:
              this.exitAuthorisation();
              break;
          }

          this.preventClicks = false;
        }
      );
  }

  public signOffAllTask(numberOfDiagrams) {
    return this.popupSvc
      .open(SignOffAuthorisationComponent, {
        panelClass: ['q9-dialog'],
        width: DialogWidth.SM,
        data: {
          all: true,
          numberOfDiagrams
        }
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(taskResponse => {
        const currentTaskResponse = this.lastSignedOffTask(taskResponse);

        switch (true) {
          case !currentTaskResponse:
            break;

          case currentTaskResponse.hasOwnProperty('movedToReady'):
            this.exitAuthorisation();
            break;

          default:
            break;
        }
      });
  }

  public signOffTask() {
    const currentTask = this.currTask$.value;
    const taskList = this.authorisationService.taskListSync();
    const allApproved = this.allTaskApproved(taskList);

    if (allApproved) {
      return this.signOffAllTask(taskList.length);
    }

    return this.popupSvc
      .open(SignOffAuthorisationComponent, {
        panelClass: ['q9-dialog'],
        width: DialogWidth.SM,
        data: {diagramId: currentTask.diagram}
      })
      .afterClosed()
      .subscribe(taskResponse => this.afterSignOffHandler(taskResponse, taskList));
  }

  private afterSignOffHandler(taskResponse, taskList) {
    if (!taskResponse) {
      return;
    }

    const taskToNavigate = taskResponse.task
      ? this.selectTaskToNavigate(taskResponse.task, taskList)
      : null;

    switch (true) {
      case taskResponse.error && this.hasNext:

      case this.hasNext:
        this.navNext();
        break;

      case taskResponse.error && !!taskToNavigate:

      case !!taskToNavigate:
        this.processNavigation(taskToNavigate.diagram);
        break;

      default:
        this.exitAuthorisation();
        break;
    }

    return;
  }

  public navPrev() {
    const arr = this.authorisationService.taskListSync();
    const currIndex = this.findCurrentDiagramIndex(arr);
    const prevDiagram = arr[currIndex - 1];

    if (prevDiagram) {
      this.processNavigation(prevDiagram.diagram);
    }
  }

  public navNext() {
    const arr = this.authorisationService.taskListSync();
    const currIndex = this.findCurrentDiagramIndex(arr);
    const nextDiagram = arr[currIndex + 1];

    if (nextDiagram) {
      this.processNavigation(nextDiagram.diagram);
    }
  }

  public processNavigation(diagramId: string): void {
    this.navigationSvc.relative(diagramId);
  }

  public getTaskListHistory(): void {
    const currentTask = this.authorisationService.currentTaskSync();
    const requestId = currentTask.connectionId._id
      ? currentTask.connectionId._id
      : currentTask.connectionId;
    const payload = {diagramId: currentTask.diagram, requestId};

    this.authorisationService
      .getTaskUsers(payload)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(history => {
        this.userHistory = history.list;
      });
  }

  private getNewTaskStatus({check, taskStatus}) {
    const openedStatuses = [
      AUTHORISATION_TASK_STATUSES_ENUM.NOT_STARTED,
      AUTHORISATION_TASK_STATUSES_ENUM.IN_PROGRESS
    ];

    if (check && openedStatuses.includes(taskStatus)) {
      return AUTHORISATION_TASK_STATUSES_ENUM.READY_TO_APPROVE;
    }

    if (!check && taskStatus === AUTHORISATION_TASK_STATUSES_ENUM.READY_TO_APPROVE) {
      return AUTHORISATION_TASK_STATUSES_ENUM.IN_PROGRESS;
    } else {
      return;
    }
  }

  private selectTaskToNavigate(currentTask, taskList: IAuthorisationTask[]) {
    _.remove(taskList, task => {
      return task.diagram === currentTask.diagram;
    });
    const sortedList = _.sortBy(taskList, task => {
      const taskOrder = {
        [AUTHORISATION_TASK_STATUSES_ENUM.NOT_STARTED]: 1,
        [AUTHORISATION_TASK_STATUSES_ENUM.IN_PROGRESS]: 2,
        [AUTHORISATION_TASK_STATUSES_ENUM.READY_TO_APPROVE]: 3
      };

      return taskOrder[task.status];
    });

    return _.head(sortedList);
  }

  public navBackToLastAuthorisationItem() {
    const arr = this.authorisationService.taskListSync();
    const diagramId = this.lastVisited ? this.lastVisited.diagramId : arr[0].diagram;
    const state = this.lastVisited
      ? this.lastVisited.state
      : DIAGRAM_VERSION_STATES_ENUM.BEING_AUTHORISED;
    const authorisationStr = this.lastVisited ? this.lastVisited.authorisation : 'true';
    const navObj = new NavObject(diagramId, state, null, null, authorisationStr);

    this.navigationSvc.navigateByObj(navObj);
  }

  public exitAuthorisation() {
    const diagramId = this.diagramJsonSvc.get().diagramId;

    this.authorisationService.wipeAuthorisation();
    this.navigationSvc.navigateWithoutQuery(diagramId, DIAGRAM_VERSION_STATES_ENUM.MASTER);
  }

  private lastSignedOffTask(taskList) {
    if (taskList) {
      return taskList.find(i => i.task.diagram === this.currTask$.value.diagram);
    }

    return;
  }

  private allTaskApproved(taskList) {
    return taskList.every(t => t.status === AUTHORISATION_TASK_STATUSES_ENUM.READY_TO_APPROVE);
  }
}
